import * as Sentry from "@sentry/react";
import {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  getDocFromServer,
} from "firebase/firestore";

const reportedCachedMissingDocumentPaths = new Set<string>();

type CachedMissingDocumentSnapshot = {
  exists(): boolean;
  metadata: {
    fromCache: boolean;
  };
};

export async function reportMissingIndexedDbDocument<
  T = DocumentData,
  B extends DocumentData = DocumentData,
>({
  ref,
  snapshot,
}: {
  ref: DocumentReference<T, B>;
  snapshot: DocumentSnapshot<T, B>;
}): Promise<void> {
  if (!isOnlineCachedMissingDocumentSnapshot(snapshot)) {
    return;
  }

  const reportKey = `${ref.firestore.app.name}:${ref.path}`;
  if (reportedCachedMissingDocumentPaths.has(reportKey)) {
    return;
  }

  const serverSnapshot = await getDocFromServer(ref).catch(() => undefined);
  if (!serverSnapshot?.exists()) {
    return;
  }

  reportedCachedMissingDocumentPaths.add(reportKey);
  Sentry.captureException(
    new Error(
      "Firestore returned a cache-backed missing document while the server document exists.",
    ),
    {
      extra: {
        browserOnline: getBrowserOnlineState(),
        documentPath: ref.path,
        firebaseAppName: ref.firestore.app.name,
        fromCache: snapshot.metadata.fromCache,
        serverExists: serverSnapshot.exists(),
      },
      fingerprint: ["firestore", "cached-missing-document"],
      tags: {
        issue: "firestore_cached_missing_document",
      },
    },
  );
}

export function isOnlineCachedMissingDocumentSnapshot(
  snapshot: CachedMissingDocumentSnapshot,
): boolean {
  return (
    getBrowserOnlineState() && snapshot.metadata.fromCache && !snapshot.exists()
  );
}

function getBrowserOnlineState(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}
